import React, { useEffect, useState } from "react";
import { Download, FileText, Image as ImageIcon, Loader2, Upload } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { batchCsvUrl, fileUrl, getBatch, errMessage } from "@/lib/api";
import { VENDOR_FIELDS } from "@/lib/fields";
import { rowIssues } from "@/lib/validation";

const KEY_FIELDS = ["company_name", "pan", "gstin", "account_number", "ifsc", "reason_comments"];

export const BatchDetailDialog = ({ batchId, open, onOpenChange, onLoad }) => {
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!open || !batchId) return undefined;
    setLoading(true);
    setError(null);
    getBatch(batchId)
      .then((d) => {
        if (alive) setBatch(d);
      })
      .catch((e) => alive && setError(errMessage(e, "Could not load this batch")))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [batchId, open]);

  const vendors = batch?.vendors || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[88vh] max-w-3xl overflow-hidden bg-card"
        data-testid="batch-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle>Saved extraction</DialogTitle>
          <DialogDescription>
            {batch
              ? `${new Date(batch.created_at).toLocaleString()} · ${batch.source_summary} · ${
                  vendors.length
                } vendor row${vendors.length === 1 ? "" : "s"}`
              : "Loading the stored email, files and rows…"}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading…
          </div>
        ) : null}
        {error ? (
          <p className="rounded-lg border border-danger-border bg-danger-surface px-3 py-2 text-sm text-danger-text">
            {error}
          </p>
        ) : null}

        {batch && !loading ? (
          <Tabs defaultValue="vendors" className="min-h-0">
            <TabsList className="bg-secondary">
              <TabsTrigger value="vendors" data-testid="detail-tab-vendors">
                Vendors ({vendors.length})
              </TabsTrigger>
              <TabsTrigger value="email" data-testid="detail-tab-email">
                Email body
              </TabsTrigger>
              <TabsTrigger value="files" data-testid="detail-tab-files">
                Files ({(batch.files || []).length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="vendors" className="mt-3 max-h-[46vh] overflow-y-auto pr-1">
              {vendors.length ? (
                <ul className="space-y-2">
                  {vendors.map((v, i) => {
                    const issues = rowIssues(v);
                    return (
                      <li
                        key={v.id || i}
                        className="rounded-lg border border-border bg-secondary/50 p-3"
                        data-testid="detail-vendor-item"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold">
                            {v.company_name || "Unnamed vendor"}
                          </p>
                          {issues.invalid ? (
                            <span className="rounded-full bg-danger-surface px-2 py-0.5 text-[10px] font-semibold text-danger-text">
                              Invalid
                            </span>
                          ) : null}
                        </div>
                        <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
                          {VENDOR_FIELDS.filter((f) => KEY_FIELDS.includes(f.key)).map((f) => (
                            <div key={f.key} className="min-w-0">
                              <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                {f.label}
                              </dt>
                              <dd
                                className={`truncate text-xs ${f.mono ? "vde-code" : ""}`}
                                title={v[f.key] || ""}
                              >
                                {v[f.key] || "—"}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No vendor rows were found in this batch.
                </p>
              )}
            </TabsContent>

            <TabsContent value="email" className="mt-3">
              {batch.email_body?.trim() ? (
                <pre
                  className="vde-code max-h-[46vh] overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-secondary/50 p-3 text-[11px] leading-relaxed text-foreground"
                  data-testid="history-email-preview"
                >
                  {batch.email_body}
                </pre>
              ) : (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No email text was pasted for this batch.
                </p>
              )}
            </TabsContent>

            <TabsContent value="files" className="mt-3 max-h-[46vh] overflow-y-auto">
              {(batch.files || []).length ? (
                <ul className="space-y-2">
                  {batch.files.map((f) => (
                    <li
                      key={f.id}
                      className="flex items-center gap-3 rounded-lg border border-border bg-secondary/50 p-2.5"
                      data-testid="detail-file-item"
                    >
                      {/\.pdf$/i.test(f.filename) ? (
                        <FileText className="h-4 w-4 shrink-0 text-danger-text" aria-hidden="true" />
                      ) : (
                        <img
                          src={fileUrl(batch.id, f.id)}
                          alt={f.filename}
                          className="h-10 w-14 shrink-0 rounded border border-border bg-card object-cover"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-medium">{f.filename}</p>
                        <p className="vde-code text-[10px] text-muted-foreground">
                          {(f.size / 1024).toFixed(0)} KB
                        </p>
                      </div>
                      <a
                        href={fileUrl(batch.id, f.id)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-card px-2.5 text-[11px] font-medium transition-colors hover:bg-secondary"
                        data-testid="detail-file-open"
                      >
                        <ImageIcon className="h-3 w-3" aria-hidden="true" /> Open
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No files were uploaded in this batch.
                </p>
              )}
            </TabsContent>
          </Tabs>
        ) : null}

        <DialogFooter className="gap-2">
          {batch ? (
            <a
              href={batchCsvUrl(batch.id)}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border bg-card px-3 text-xs font-medium transition-colors hover:bg-secondary"
              data-testid="detail-csv-button"
            >
              <Download className="h-3.5 w-3.5" aria-hidden="true" /> Download CSV
            </a>
          ) : null}
          <Button
            className="gap-1.5"
            disabled={!batch}
            onClick={() => {
              onLoad(batch.id);
              onOpenChange(false);
            }}
            data-testid="detail-load-button"
          >
            <Upload className="h-3.5 w-3.5" aria-hidden="true" /> Load into grid
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BatchDetailDialog;
